import React, { useState, useMemo, useCallback, memo } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useMessaging } from '../../contexts/MessagingContext';
import Button from '../ui/Button';

const formatLastMessageTime = (timestamp) => {
  const now = new Date();
  const messageTime = new Date(timestamp);
  const diffInMinutes = Math.floor((now - messageTime) / (1000 * 60));
  
  if (diffInMinutes < 1) return 'Just now';
  if (diffInMinutes < 60) return `${diffInMinutes}m`;
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`;
  if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)}d`;
  
  return messageTime.toLocaleDateString();
};

// Memoized individual conversation row to prevent full list re-renders
const ConversationItem = memo(({ conversation, isSelected, currentUserId, onSelect }) => (
  <div
    onClick={() => onSelect(conversation)}
    className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
      isSelected ? 'bg-blue-50 border-r-2 border-blue-500' : ''
    }`}
  >
    <div className="flex items-start space-x-3">
      {/* Avatar */}
      <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0">
        {(conversation.participant_name || 'Unknown').charAt(0).toUpperCase()}
      </div>

      {/* Conversation Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-semibold text-gray-900 truncate">
            {conversation.participant_name || 'Unknown User'}
          </h3>
          <div className="flex items-center space-x-2">
            {conversation.unread_count > 0 && (
              <span className="bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                {conversation.unread_count}
              </span>
            )}
            <span className="text-xs text-gray-500">
              {conversation.last_message_at ? formatLastMessageTime(conversation.last_message_at) : ''}
            </span>
          </div>
        </div>

        <p className="text-sm text-gray-600 mb-1">
          {conversation.company_name || ''} {conversation.company_name && conversation.job_title ? '•' : ''} {conversation.job_title || 'No job specified'}
        </p>

        <div className="flex items-center">
          <p className="text-sm text-gray-700 truncate flex-grow">
            {conversation.last_sender_id === currentUserId && (
              <span className="text-gray-500">You: </span>
            )}
            {conversation.last_message || 'No messages yet'}
          </p>
        </div>
      </div>
    </div>
  </div>
));

ConversationItem.displayName = 'ConversationItem';

const MessagesList = ({ onSelectConversation, selectedConversationId }) => {
  const { user: currentUser } = useAuth();
  const { conversations, isLoading: loading } = useMessaging();
  const [filter, setFilter] = useState('all'); // all, unread, candidates

  // Ensure conversations is an array and memoize derived state
  const safeConversations = useMemo(
    () => Array.isArray(conversations) ? conversations : [],
    [conversations]
  );
  
  const filteredConversations = useMemo(() => safeConversations.filter(conv => {
    switch (filter) {
      case 'unread':
        return conv.unread_count > 0;
      case 'candidates':
        return conv.participant_role === 'va';
      default:
        return true;
    }
  }), [safeConversations, filter]);

  const totalUnreadCount = useMemo(
    () => safeConversations.reduce((sum, conv) => sum + (conv.unread_count || 0), 0),
    [safeConversations]
  );

  // Stable callback reference for conversation selection
  const handleSelect = useCallback((conversation) => {
    onSelectConversation(conversation);
  }, [onSelectConversation]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Messages</h2>
          {totalUnreadCount > 0 && (
            <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
              {totalUnreadCount}
            </span>
          )}
        </div>

        {/* Filter Tabs */}
        <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
          {[
            { key: 'all', label: 'All', count: safeConversations.length },
            { key: 'unread', label: 'Unread', count: totalUnreadCount },
            { key: 'candidates', label: 'Candidates', count: safeConversations.filter(c => c.participant_role === 'va').length }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                filter === tab.key
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className="ml-1 text-xs">({tab.count})</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Conversations List */}
      <div className="flex-1 overflow-y-auto">
        {filteredConversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <div className="text-4xl mb-4">💬</div>
            <p className="text-lg font-medium">No conversations yet</p>
            <p className="text-sm">Start applying to jobs to connect with employers!</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredConversations.map((conversation) => (
              <ConversationItem
                key={conversation.conversation_id}
                conversation={conversation}
                isSelected={selectedConversationId === conversation.conversation_id}
                currentUserId={currentUser?.id}
                onSelect={handleSelect}
              />
            ))}
          </div>
        )}
      </div>

      {/* New Message Button */}
      <div className="p-4 border-t border-gray-200">
        <Button
          variant="outline"
          fullWidth
          onClick={() => {
            // TODO: Implement new message functionality
            // TODO: Implement new conversation creation
          }}
        >
          ✉️ New Message
        </Button>
      </div>
    </div>
  );
};

export default MessagesList;

